/**
 * Inline notice pill rendered at the tail of an assistant turn.
 *
 * Used wherever a turn finished in a non-default state — an upstream
 * error, an early stop reason — and a short informational marker is
 * the right register. Visual sibling of the "Interrupted" pill in
 * :class:`AssistantStream`: small, icon + sentence, sits in the
 * same column rhythm so it reads as the agent's own footnote rather
 * than a banner pasted on top.
 *
 * Tone bands:
 *   - ``error``    — red, used for genuine failures
 *   - ``warning``  — amber, used for recoverable interruptions
 *     (token limits, rate limits, refusals)
 *   - ``neutral``  — muted gray, used for benign / informational
 *     stops (``pause_turn``, generic ``stop_sequence``)
 *
 * The raw underlying error string (when present) is exposed as the
 * pill's ``title`` so power users can still see the source on
 * hover.
 */

import type { AgentNotice, NoticeTone } from '@/lib/agent/errors';
import { cn } from '@/lib/utils/cn';

export interface TurnNoticePillProps {
  notice: AgentNotice;
  className?: string;
}

const TONE_CLASSES: Record<NoticeTone, { wrap: string; icon: string; hint: string }> = {
  error: {
    wrap: 'border-red-200 bg-red-50/70 text-red-800 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-200',
    icon: 'text-red-500 dark:text-red-400',
    hint: 'text-red-700/80 dark:text-red-200/80',
  },
  warning: {
    wrap: 'border-amber-200 bg-amber-50/70 text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-100',
    icon: 'text-amber-500 dark:text-amber-300',
    hint: 'text-amber-800/80 dark:text-amber-100/80',
  },
  neutral: {
    wrap: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300',
    icon: 'text-gray-400 dark:text-gray-500',
    hint: 'text-gray-500 dark:text-gray-400',
  },
};

export function TurnNoticePill({ notice, className }: TurnNoticePillProps) {
  const tone = TONE_CLASSES[notice.tone];
  const Icon = notice.icon;
  return (
    <div
      role="status"
      title={notice.raw}
      className={cn(
        'flex w-fit max-w-full select-text items-start gap-2 rounded-md border px-2.5 py-1.5 text-[12.5px] leading-snug',
        tone.wrap,
        className
      )}
    >
      <Icon className={cn('mt-[1px] size-3.5 shrink-0 select-none', tone.icon)} aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="font-medium">{notice.headline}</span>
        {notice.hint && <span className={cn('ml-1.5', tone.hint)}>{notice.hint}</span>}
      </div>
    </div>
  );
}
